import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Dumbbell, TrendingUp, Users, DollarSign, Clock,
  CheckCircle, CalendarDays, AlertCircle,
  Phone, Mail, User,
} from 'lucide-react';
import { feeUnit } from '../../../utils/helpers';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12} ${suffix}` : `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
};

const ProgramOverviewPanel = ({
  program,
  enrolled,
  seatsAvailable,
  onRegister,
}) => {
  const { theme } = useTheme();

  const sessionCount = (program.classes || []).length;
  const uniqueDays = [...new Set(
    (program.classes || []).flatMap(c => c.recurrence ? c.recurrence.split('/') : [])
  )];

  const startFmt = program.startDate ? formatDate(program.startDate) : null;
  const endFmt   = program.endDate   ? formatDate(program.endDate)   : null;
  const timeFmt  = (program.timeFrom || program.timeTo)
    ? `${program.timeFrom ? formatTime(program.timeFrom) : '—'} – ${program.timeTo ? formatTime(program.timeTo) : '—'}`
    : null;

  /* WHAT IT COSTS COMES FROM THE PRICE TAG.
   *
   * `fee` and `late_fee` were columns on the programme that the API read and
   * wrote and no payment path ever charged. The tag is what checkout bills, and
   * its late fee is an amount plus how many days before the start it begins —
   * two columns, because a tag is shared and cannot carry one programme's
   * dates. */
  const feeDisplay = program.price?.amount != null
    ? `$${Number(program.price.amount).toFixed(0)}${feeUnit(program, { long: true })}`
    : null;
  const lateFeeDisplay = program.price?.lateFeeAmount != null
    ? `$${Number(program.price.lateFeeAmount).toFixed(0)}${
        program.price.lateFeeDaysBefore
          ? ` from ${program.price.lateFeeDaysBefore} days before the start`
          : ' after deadline'}`
    : null;
  const enrolledCount = program.enrollmentCount ?? 0;
  const capacityDisplay = program.maxParticipants != null
    ? `${enrolledCount} / ${program.maxParticipants} enrolled`
    : enrolledCount > 0
      ? `${enrolledCount} enrolled · Unlimited`
      : null;

  const detailItems = [
    { label: 'Sport',        value: program.sportName, Icon: Dumbbell },
    { label: 'Level',        value: program.level ? program.level.charAt(0).toUpperCase() + program.level.slice(1) : null, Icon: TrendingUp },
    { label: 'Age Group',    value: program.ageGroup, Icon: Users },
    { label: 'Monthly Fee',  value: feeDisplay, Icon: DollarSign },
    ...(startFmt || endFmt ? [{ label: 'Duration', value: `${startFmt || '—'} – ${endFmt || '—'}`, Icon: CalendarDays }] : []),
    ...(timeFmt ? [{ label: 'Session Time', value: timeFmt, Icon: Clock }] : []),
    ...(capacityDisplay ? [{ label: 'Capacity', value: capacityDisplay, Icon: Users }] : []),
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left — main content */}
      <div className="lg:col-span-2 space-y-5">
        {/* Hero image */}
        {program.image && (
          <img
            src={program.image}
            alt={program.name}
            className="h-52 w-full object-cover rounded-xl"
          />
        )}

        {/* Description */}
        <div className={`${theme.card} rounded-xl border ${theme.border} p-5`}>
          <h3 className={`text-base font-semibold ${theme.text} mb-2`}>About this Program</h3>
          <p className={`text-sm ${theme.textSecondary} leading-relaxed`}>{program.description}</p>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-3">
          {detailItems.map(({ label, value, Icon }) => (
            <div
              key={label}
              className={`${theme.card} rounded-xl border ${theme.border} p-4 flex items-start gap-3`}
            >
              <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                <Icon size={16} className="text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className={`text-xs ${theme.textMuted} mb-0.5`}>{label}</p>
                <p className={`text-sm font-semibold ${theme.text} truncate`}>{value || '—'}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Sessions summary */}
        {sessionCount > 0 && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border ${theme.border} ${theme.card}`}>
            <Clock size={15} className="text-blue-500 shrink-0" />
            <p className={`text-sm ${theme.textSecondary}`}>
              <span className="font-semibold">{sessionCount}</span> session{sessionCount !== 1 ? 's' : ''}/week
              {uniqueDays.length > 0 && (
                <> across <span className="font-semibold">{uniqueDays.length}</span> day{uniqueDays.length !== 1 ? 's' : ''}</>
              )}
            </p>
          </div>
        )}

        {/* Contact info */}
        {(program.contactName || program.contactEmail || program.contactPhone) && (
          <div className={`${theme.card} rounded-xl border ${theme.border} p-5`}>
            <h3 className={`text-sm font-semibold ${theme.text} mb-3`}>Contact</h3>
            <div className="space-y-2">
              {program.contactName && (
                <div className="flex items-center gap-2">
                  <User size={14} className={theme.textMuted} />
                  <span className={`text-sm ${theme.textSecondary}`}>{program.contactName}</span>
                </div>
              )}
              {program.contactEmail && (
                <div className="flex items-center gap-2">
                  <Mail size={14} className={theme.textMuted} />
                  <a href={`mailto:${program.contactEmail}`} className="text-sm text-blue-600 hover:underline">
                    {program.contactEmail}
                  </a>
                </div>
              )}
              {program.contactPhone && (
                <div className="flex items-center gap-2">
                  <Phone size={14} className={theme.textMuted} />
                  <a href={`tel:${program.contactPhone}`} className={`text-sm ${theme.textSecondary} hover:underline`}>
                    {program.contactPhone}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right — Registration sidebar */}
      <div>
        <div className={`${theme.card} rounded-xl border ${theme.border} p-5 space-y-4`}>
          <div>
            <p className={`text-3xl font-bold ${theme.text}`}>{feeDisplay || '—'}</p>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>
              {seatsAvailable === null
                ? 'Unlimited capacity'
                : `${seatsAvailable} seat${seatsAvailable !== 1 ? 's' : ''} available`}
            </p>
          </div>

          {/* Registration status pill */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              program.registrationOpen ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {program.registrationOpen ? 'Open' : 'Closed'}
            </span>
          </div>

          {/* Due date + late fee */}
          {(program.registrationDueDate || program.lateFee) && (
            <div className={`space-y-2 pt-1 border-t ${theme.border}`}>
              {program.registrationDueDate && (
                <div className="flex items-center justify-between">
                  <span className={`flex items-center gap-1.5 text-xs ${theme.textSecondary}`}>
                    <CalendarDays size={13} className="flex-shrink-0" />
                    Registration due
                  </span>
                  <span className={`text-xs font-semibold ${theme.text}`}>
                    {formatDate(program.registrationDueDate)}
                  </span>
                </div>
              )}
              {lateFeeDisplay && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs text-amber-600">
                    <AlertCircle size={13} className="flex-shrink-0" />
                    Late fee
                  </span>
                  <span className="text-xs font-semibold text-amber-600">
                    {lateFeeDisplay}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Actions — management controls (open/close registration, edit,
              create flyer) deliberately live only in Settings → Programs.
              This panel is a read-only detail view, so everyone (including
              owners/admins) sees the same registration CTA here. */}
          {enrolled ? (
            // Being enrolled yourself must not remove the ability to register
            // someone else — e.g. a managed player added after you signed up.
            <div className="space-y-2">
              <div className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-100 text-green-700 rounded-lg text-sm font-semibold">
                <CheckCircle size={16} />
                You are enrolled
              </div>
              {program.registrationOpen && seatsAvailable !== 0 && (
                <button
                  onClick={onRegister}
                  className={`w-full px-4 py-2.5 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm font-semibold transition`}
                >
                  Register another player
                </button>
              )}
            </div>
          ) : program.registrationOpen ? (
            <button
              onClick={onRegister}
              className={`w-full px-4 py-2.5 ${theme.buttonPrimary} rounded-lg text-sm font-semibold transition`}
            >
              Register Now
            </button>
          ) : (
            <button
              disabled
              className="w-full px-4 py-2.5 bg-gray-200 text-gray-400 rounded-lg text-sm font-semibold cursor-not-allowed"
            >
              Registration Closed
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgramOverviewPanel;
